import apiFetch from "@/src/utils/apiFetch";

import {
  closeDrawer,
  openConfirmDialog,
  openDrawer,
} from "@/src/components/dialogs";

import {
  AppointmentType,
  Reference,
  referenceCategories,
} from "@/src/data/allTypes";

import { ReorderableItems } from "@/src/components/reorderable";

import { Button } from "@/src/components/buttons";
import { Card, CompartmentCard } from "@/src/components/cards";
import { ColorCircle } from "@/src/components/colorCircle";
import { FetchableList } from "@/src/components/fetchableLists";
import { Input } from "@/src/components/universalInput";
import { getItemId, getUserId, uid } from "@/src/utils/utils";
import { useState } from "react";
import { MdOutlineDragIndicator } from "react-icons/md";
import AdminReferencesCompartment from "./adminReferencesCompartment";
import { AppointmentTypeForm } from "../../appointments/appointmentTypeForm";
import { AppointmentTypesChoiceList } from "../../appointments/appointmentTypesChoiceList";
import { ReferenceChoiceList } from "./referenceChoiceList";

function AdminAppointments() {
  return (
    <div className="grid c2 mc1 gap25">
      <AdminAppointmentTypes></AdminAppointmentTypes>
      <AdminReferencesCompartment
        category={referenceCategories.appointmentPreparation}
      ></AdminReferencesCompartment>
    </div>
  );
}

function AdminAppointmentTypes({
  parentReference,
}: {
  parentReference?: Reference;
}) {
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState("");

  async function openAppointmentTypeForm(appointmentType: AppointmentType) {
    await openDrawer({
      id: "appointmentTypeDialog",
      title: "Ajouter un type de rendez-vous",
      content: (
        <AppointmentTypeForm
          current={appointmentType}
          onChange={(newValue) => {
            appointmentType = newValue;
          }}
        />
      ),
      onConfirm: async () => {
        let inserted = await apiFetch(
          "appointmentTypes",
          "addOne",
          { appointmentType },
          "Erreur lors de l'ajout du type de rendez-vous"
        );
        setReload((prev) => prev + 1);
      },
    });
  }

  return (
    <CompartmentCard
      title={"Types de rendez-vous"}
      searchKey={searchKey}
      onSearch={(newValue) => {
        setSearchKey(newValue || "");
      }}
      onClick={async () => {
        let appointmentType: AppointmentType | null = null;
        await openDrawer({
          id: "appointmentTypeDialog",
          title: "Ajouter un type de rendez-vous",
          content: (
            <AppointmentTypesChoiceList
              currentAppointmentTypes={[]}
              onSelect={async (newValue) => {
                appointmentType = newValue;
                appointmentType.exam = parentReference;
                let inserted = await apiFetch(
                  "appointmentTypes",
                  "addOne",
                  { appointmentType },
                  "Erreur lors de l'ajout du type de rendez-vous"
                );
                setReload((prev) => prev + 1);
                closeDrawer("appointmentTypeDialog");
              }}
            />
          ),
        });
      }}
    >
      <FetchableList
        queryKey={[
          "AdminAppointmentTypes",
          searchKey,
          parentReference?.id,
          uid(),
          reload,
        ]}
        queryFn={() =>
          apiFetch(
            "appointmentTypes",
            "loadMany",
            { searchKey, examId: getItemId(parentReference) },
            "Erreur lors du chargement des types de rendez-vous"
          )
        }
      >
        {(data) => {
          return (
            <div className="grid c1 gap10 ">
              {data?.map((appointmentType) => {
                return (
                  <Card
                    key={uid()}
                    color="blue"
                    leading={
                      <ColorCircle color={appointmentType.color}></ColorCircle>
                    }
                    title={appointmentType.name}
                    subTitle={appointmentType.description}
                    onClick={async () => {
                      console.log("appointmentType", appointmentType);
                      await openAppointmentTypeForm(appointmentType);
                    }}
                    onDelete={async () => {
                      await openConfirmDialog({
                        id: "deleteAppointmentType",
                        content:
                          "Voulez-vous vraiment supprimer ce type de rendez-vous ?",
                        onConfirm: async () => {
                          await apiFetch(
                            "general",
                            "deleteAny",
                            {
                              id: getItemId(appointmentType.id),
                              table: "appointmentTypes",
                            },
                            "Erreur lors de la suppression du type de rendez-vous"
                          );
                          setReload((prev) => prev + 1);
                        },
                      });
                    }}
                  ></Card>
                );
              })}
            </div>
          );
        }}
      </FetchableList>
    </CompartmentCard>
  );
}

function AppointmentTypePicker({
  title,
  value,
  onChange,
}: {
  title: string;
  value: AppointmentType | null | undefined;
  onChange: (newValue: AppointmentType) => void;
}) {
  const [current, setCurrent] = useState(value);
  return (
    <div
      className="field"
      onClick={async () => {
        await openDrawer({
          id: "referenceIdPicker",
          title: "Choisir un élément",
          content: (
            <AppointmentTypesChoiceList
              currentAppointmentTypes={current ? [current] : []}
              onSelect={async (newValue) => {
                onChange(newValue);
                setCurrent(newValue);
                closeDrawer("referenceIdPicker");
              }}
            />
          ),
        });
      }}
    >
      <div className="label">{title}</div>
      <div className="card">
        {current?.name ? <strong>{current.name}</strong> : "Non défini"}
      </div>
    </div>
  );
}

function ReorderableAppointmentPreparations({
  current,
  onChange,
}: {
  current: (Reference | null | undefined)[];
  onChange: (newValue: (Reference | null | undefined)[]) => void;
}) {
  let [references, setReferences] = useState(current);

  return (
    <div className="field">
      <label>{"Préparations du patient"}</label>
      <div className="grid c1 gap10">
        <ReorderableItems
          items={references || []}
          listId="permissions"
          listType={"permissions"}
          onReorder={(newItems) => {
            onChange(newItems);
            setReferences(newItems);
          }}
        >
          {references?.map((reference) => {
            if (!reference) return null;

            return (
              <Card
                key={uid()}
                color="blue"
                title={reference.name}
                leading={
                  <div className="grab disabled">
                    <MdOutlineDragIndicator size={15} />
                  </div>
                }
                onEdit={async () => {
                  if (!reference) return;
                  await openDrawer({
                    title: "Modifier une référence",
                    content: (
                      <AppointmentPreparationForm
                        current={reference}
                        onChange={(newValue) => {
                          reference = newValue;
                        }}
                      />
                    ),
                    onConfirm: async () => {
                      if (!reference) return;
                      let inserted = await apiFetch("references", "addOne", {
                        reference,
                      });
                      if (!inserted) return;
                      let data = [...references];

                      data.push(inserted);
                      setReferences(data);
                      onChange(data!);
                    },
                  });
                }}
                onDelete={async () => {
                  setReferences([
                    ...references.filter((ref) => ref?.id != reference?.id),
                  ]);
                  onChange([
                    ...references.filter((ref) => ref?.id != reference?.id),
                  ]);
                }}
              ></Card>
            );
          })}
        </ReorderableItems>
        <Button
          type={"tertiary"}
          onClick={async () => {
            let reference: Reference = {
              name: "",
              category: "appointmentPreparation",
              sections: [],
              creator: getUserId(),
            };

            await openDrawer({
              id: "referenceDialog",
              title: "Choisir une référence",
              content: (
                <ReferenceChoiceList
                  current={references}
                  categories={["appointmentPreparation"]}
                  onSelect={async (newValue) => {
                    closeDrawer("referenceDialog");
                    if (!reference) return;
                    reference = newValue;
                    let inserted = await apiFetch("references", "addOne", {
                      reference,
                    });
                    if (!inserted) return;
                    let data = [...references];

                    data.push(inserted);
                    setReferences(data);
                    onChange(data!);
                  }}
                />
              ),
              footer: <div></div>,
            });
          }}
        >
          Ajouter une préparation
        </Button>
      </div>
    </div>
  );
}

function AppointmentPreparationForm({
  current,
  onChange,
}: {
  current: Reference;
  onChange: (newValue: Reference) => void;
}) {
  let [reference, setReference] = useState(current || ({} as Reference));
  function setNewValue(newValue: Reference) {
    setReference({ ...newValue });
    onChange({ ...newValue });
  }

  return (
    <div className="grid c1 gap10">
      <Input
        name={"Nom"}
        type={"text"}
        value={reference.name}
        placeholder="Ex: Patient à jeun"
        onChange={(newValue) => {
          setNewValue({ ...reference, name: newValue });
        }}
      />
      <Input
        name={"Texte (imprimable)"}
        type={"textarea"}
        value={reference.description}
        onChange={(newValue) => {
          setNewValue({ ...reference, description: newValue });
        }}
      />
    </div>
  );
}

export {
  AdminAppointments,
  AdminAppointmentTypes,
  AppointmentTypeForm,
  AppointmentTypePicker,
  ReorderableAppointmentPreparations,
};
